/**
 * Revert by transaction (ADR-009 §6.2).
 *
 * Reverting applies a *new* forward transaction that compensates an earlier
 * one, wherever it sits in history, and is itself one undoable entry
 * ("Revert: <label>"). It never merges: it runs only when the journal proves
 * that nothing later depends on what it undoes, and otherwise refuses with
 * REVERT_CONFLICT, naming the fields and who changed them.
 *
 * The check has two levels:
 *
 * - Field by field, always: every journaled property must still hold the
 *   value the transaction left (`current(layer, path) == change.after`).
 *   For transactions made only of commands whose undo restores exactly the
 *   fields they changed (layer properties, mask flags, transforms, layer
 *   styles), this is the whole check, so a later edit to a *different* field
 *   of the same layer does not block the revert and survives it.
 * - Whole footprint, when field values cannot prove safety: structural
 *   changes (creations, deletions, moves, grouping), pixel changes, and
 *   commands whose undo restores a whole object (text, vector, adjustment
 *   settings). Then no later revision in the ledger may touch the
 *   transaction's layers or their parents.
 *
 * The compensation itself is the transaction's own native commands undone
 * out of order (safe because of the check) and re-applied when the revert
 * is undone. The reverted transaction is pinned so history eviction cannot
 * dispose commands the revert still needs.
 */
import { contentLabel, type ActorRef, type LayerId, type RejectionCode } from '@maipo/agent-protocol';
import { SetLayerTransformCommand, UpdateLayerCommand, UpdateMaskCommand, type Command } from '../core/commands';
import { UpdateLayerStyleCommand } from '../core/layer-styles';
import { findLayer } from '../core/model';
import type { DocumentSession } from '../core/session';
import { currentRecord, DOCUMENT_JOURNAL_ID } from './journal';
import { footprintFromTransaction, normalizeLayerId } from './ledger';
import { checkUnlocked } from './intents/common';
import { policyFor, type Diagnostic } from './registry';
import { InverseTransactionStep, TransactionCommand, type TxMeta } from './transaction';
import { commitLocalTransaction, localTxId } from './user-dispatch';

/** Native commands whose undo restores exactly the fields they changed (and nothing else). */
const FIELD_PRECISE: readonly (abstract new (...args: never[]) => Command)[] = [UpdateLayerCommand, UpdateMaskCommand, SetLayerTransformCommand, UpdateLayerStyleCommand];

const STRUCTURAL_PATHS = new Set(['(created)', '(deleted)', 'parentId', 'index']);

export interface RevertConflict {
  layer: LayerId;
  /** The field that changed since, or absent when a later revision touched the layer as a whole. */
  path?: string;
  /** Who changed it, when the ledger still knows. */
  actor?: ActorRef;
  label?: string;
  revision?: number;
}

export type RevertOutcome =
  | { status: 'committed'; command: TransactionCommand }
  | { status: 'rejected'; code: RejectionCode; message: string; conflicts?: RevertConflict[] };

function actorName(actor: ActorRef): string {
  switch (actor.kind) {
    case 'user': return 'you';
    case 'agent': return `agent ${actor.client}`;
    case 'addon': return `add-on ${actor.id}`;
    case 'replay': return `action ${actor.action}`;
  }
}

function describeConflicts(conflicts: readonly RevertConflict[], nameOf: (layer: LayerId) => string): string {
  const parts = conflicts.slice(0, 4).map((conflict) => `${conflict.path ? `${conflict.path} of “${nameOf(conflict.layer)}”` : `“${nameOf(conflict.layer)}”`}${conflict.actor ? ` (changed by ${actorName(conflict.actor)}${conflict.label ? `: “${conflict.label}”` : ''})` : ''}`);
  return `${parts.join('; ')}${conflicts.length > 4 ? `; and ${conflicts.length - 4} more` : ''}`;
}

function refuse(code: RejectionCode, message: string, conflicts?: RevertConflict[]): RevertOutcome {
  return { status: 'rejected', code, message, ...(conflicts?.length ? { conflicts } : {}) };
}

/** Whether an entry is a journaled transaction still held in history (cheap; the History panel asks for every row). */
export function offersRevert(command: Command | undefined): command is TransactionCommand {
  return command instanceof TransactionCommand && !command.evicted && command.meta.journal.length > 0;
}

/** Whether a history entry can be offered for revert at all (a transaction still in the undoable past). */
export function isRevertible(session: DocumentSession, command: Command): command is TransactionCommand {
  return offersRevert(command) && session.history.find((candidate) => candidate === command)?.stack === 'past';
}

export interface RevertOptions {
  /** Who reverts. Default the user (the History panel). Agents and add-ons also get their lock policy. */
  actor?: ActorRef;
  /** Transaction ID of the compensating transaction. Default a fresh local ID. */
  txId?: string;
  /** History label, before the actor prefix. Default "Revert: <label>". */
  label?: string;
}

export function revertTransaction(session: DocumentSession, target: TransactionCommand, options: RevertOptions = {}): RevertOutcome {
  const { document, history, ledger } = session;
  const meta = target.meta;
  const actor = options.actor ?? { kind: 'user' };
  const located = history.find((candidate) => candidate === target);
  if (!located || target.evicted) return refuse('TX_EVICTED', `“${target.label}” is no longer in history, so it cannot be reverted.`);
  if (located.stack === 'future') return refuse('COMMAND_REJECTED', `“${target.label}” is undone; redo it before reverting it.`);
  if (history.hasLiveCommand) return refuse('USER_EDITING', 'Finish the current edit before reverting.');
  if (meta.journal.length === 0) return refuse('COMMAND_REJECTED', `“${target.label}” has no recorded changes to revert.`);

  // Agents and add-ons may not change locked layers by reverting either (ADR-009 §5.2);
  // the user keeps native semantics (the History panel never checked locks).
  const locks: Diagnostic[] = [];
  const policy = policyFor(actor);
  for (const id of new Set([...meta.journal.map((change) => change.layer), ...meta.footprint.writes])) {
    const layer = id === 'root' ? undefined : findLayer(document.root, id);
    if (layer) checkUnlocked(document, layer, 'any', locks, policy);
  }
  const locked = locks.find((diagnostic) => diagnostic.severity === 'error');
  if (locked) return refuse('LAYER_LOCKED', `Cannot revert “${target.label}”: ${locked.message}`);

  const committedAt = ledger.revisionOf(meta.txId);
  const later = committedAt === undefined || !ledger.covers(committedAt) ? undefined : ledger.entriesAfter(committedAt).filter((entry) => entry.txId !== meta.txId);
  const lastTouch = (layer: LayerId) => later?.slice().reverse().find((entry) => entry.footprint.unknown || entry.footprint.document || entry.footprint.layers.includes(layer));

  // 1. Field by field against the journal.
  const conflicts: RevertConflict[] = [];
  const records = new Map<LayerId, Record<string, unknown> | undefined>();
  const recordOf = (layer: LayerId) => {
    if (!records.has(layer)) records.set(layer, currentRecord(document, layer));
    return records.get(layer);
  };
  let needsFootprintCheck = !target.childCommands().every((child) => FIELD_PRECISE.some((type) => child instanceof type));
  // Document-level changes (canvas geometry, guides, notes, selection, other document
  // resources) are restored wholesale by their commands' undo, and geometry undo puts
  // back every layer's transform. Out of order that would discard later edits, so such
  // a transaction is revertible only while nothing at all came after it.
  const documentLevel = meta.footprint.document || meta.footprint.writes.some((id) => id.startsWith('#')) || meta.journal.some((change) => change.layer === DOCUMENT_JOURNAL_ID);
  if (documentLevel && (later === undefined || later.length > 0)) {
    const next = later?.[0];
    return refuse('REVERT_CONFLICT', `“${target.label}” changed the document as a whole${next ? ` and “${next.label}” came after it` : ''}; it can only be reverted while it is the latest change. Use Undo instead.`, next ? [{ layer: DOCUMENT_JOURNAL_ID, actor: next.actor, label: next.label, revision: next.revision }] : undefined);
  }
  for (const change of meta.journal) {
    if (change.layer === DOCUMENT_JOURNAL_ID) continue;
    if (STRUCTURAL_PATHS.has(change.path) || change.path.endsWith('.pixels')) { needsFootprintCheck = true; continue; }
    const record = recordOf(change.layer);
    const current = record ? JSON.stringify(record[change.path] ?? null) : undefined;
    if (current === JSON.stringify(change.after ?? null)) continue;
    const touch = lastTouch(change.layer);
    conflicts.push({ layer: change.layer, path: change.path, ...(touch ? { actor: touch.actor, label: touch.label, revision: touch.revision } : {}) });
  }

  // 2. Whole footprint, where field values cannot prove that undoing is safe.
  if (needsFootprintCheck) {
    if (later === undefined) return refuse('REVERT_CONFLICT', `“${target.label}” is too old for the revision ledger to prove that nothing later depends on it. Use Undo instead.`, conflicts);
    const layers = new Set<LayerId>([
      ...footprintFromTransaction(document, meta.footprint, Object.values(meta.aliases)).layers,
      ...meta.journal.map((change) => normalizeLayerId(document, change.layer)),
    ]);
    for (const entry of later) {
      const print = entry.footprint;
      const overlap = print.unknown || print.document ? [...layers].filter((id) => id !== 'root').slice(0, 1) : print.layers.filter((id) => layers.has(id));
      for (const layer of overlap) if (!conflicts.some((conflict) => conflict.layer === layer && !conflict.path)) conflicts.push({ layer: layer === 'root' ? document.root.id : layer, actor: entry.actor, label: entry.label, revision: entry.revision });
    }
  }
  if (conflicts.length > 0) {
    return refuse('REVERT_CONFLICT', `Cannot revert “${target.label}”: later edits changed ${describeConflicts(conflicts, (layer) => contentLabel(findLayer(document.root, layer)?.name ?? layer, 60))}. Nothing was changed.`, conflicts);
  }

  const created = meta.journal.filter((change) => change.path === '(created)').map((change) => change.layer);
  const revert: TxMeta = {
    txId: options.txId ?? localTxId('revert'),
    actor,
    label: contentLabel(options.label ?? `Revert: ${target.label}`, 80),
    createdAt: new Date().toISOString(),
    baseRevision: session.currentRevision,
    intents: [],
    footprint: { ...meta.footprint, writes: [...new Set([...meta.footprint.writes, ...Object.values(meta.aliases), ...created])] },
    journal: [],
    aliases: {},
    reverts: meta.txId,
  };
  // Selection follows the native commands' own undo, which puts back the selection they changed.
  const outcome = commitLocalTransaction(session, revert, [{ intentIndex: 0, build: () => [new InverseTransactionStep(target)] }], { restoreSelection: false, nativeSelection: true });
  if (outcome.status === 'committed') return { status: 'committed', command: outcome.command };
  if (outcome.status === 'no-op') return refuse('COMMAND_REJECTED', `Reverting “${target.label}” changed nothing.`);
  return refuse(outcome.code, outcome.message);
}
